import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { Upload, message, Button } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { getIcons, uploadIcon } from "../actions/stampcard_action";
import { useTranslation } from "react-i18next";

const IconSelector = ({
  onChange,
  theme,
  displayCustomUpload = true,
  defaultAssets = {},
}) => {
  const { t } = useTranslation();
  const accUrn = useSelector((state) => state.account.urn);
  const businessSlug = useSelector((state) => state.account.businessSlug);

  const fetchIconsByType = useCallback(async ({ queryKey }) => {
    const [_key, accUrn, type, theme] = queryKey;
    const res = await getIcons({ account_urn: accUrn, type, theme });
    return res || [];
  }, []);

  const targetQuery = useQuery({
    queryKey: ["icons", accUrn, "target", theme],
    queryFn: fetchIconsByType,
    enabled: !!accUrn && !!theme,
    staleTime: 60 * 3000,
  });

  const inkingQuery = useQuery({
    queryKey: ["icons", accUrn, "inking", theme],
    queryFn: fetchIconsByType,
    enabled: !!accUrn && !!theme,
    staleTime: 60 * 3000,
  });

  const [selectedTarget, setSelectedTarget] = useState(
    defaultAssets.targetIcon || null
  );
  const [selectedInking, setSelectedInking] = useState(
    defaultAssets.inkingIcon || null
  );

  useEffect(() => {
    if (onChange) {
      onChange({ target: selectedTarget, inking: selectedInking });
    }
  }, [selectedTarget, selectedInking]);

  useEffect(() => {
    if (defaultAssets.targetIcon) setSelectedTarget(defaultAssets.targetIcon);
    if (defaultAssets.inkingIcon) setSelectedInking(defaultAssets.inkingIcon);
  }, [defaultAssets]);

  const handleSelect = (icon, type) => {
    if (type === "target") setSelectedTarget(icon);
    if (type === "inking") setSelectedInking(icon);
  };

  const handleUpload = async (file, type) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);

    try {
      const res = await uploadIcon({ data: formData, accUrn, businessSlug });
      const { fileName } = res || {};
      if (!fileName) throw new Error(t("icon_upload_failed"));

      const localUrl = URL.createObjectURL(file);
      const uploadedIcon = { fileName, url: localUrl };

      if (type === "target") {
        targetQuery.refetch();
        setSelectedTarget(uploadedIcon);
      } else {
        inkingQuery.refetch();
        setSelectedInking(uploadedIcon);
      }

      message.success(t("icon_uploaded", { type }));
    } catch (err) {
      message.error(t("icon_upload_error", { type }));
    }

    return false;
  };

  const renderIcons = (icons = [], selected, type, isLoading, isError) => {
    if (isLoading) return <p>{t("loading_icons", { type })}</p>;
    if (isError) return <p>{t("error_loading_icons", { type })}</p>;

    return (
      <div className="icon-group" style={{ marginBottom: 24 }}>
        <h4>{type === "target" ? t("target_icon") : t("inking_icon")}</h4>

        {displayCustomUpload && (
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={(file) => {
              if (!file.type.startsWith("image/")) {
                message.error(t("only_images_allowed"));
                return Upload.LIST_IGNORE;
              }
              handleUpload(file, type);
              return false;
            }}
          >
            <Button icon={<UploadOutlined />}>
              {t("upload_icon", { type })}
            </Button>
          </Upload>
        )}

        <div
          style={{
            marginTop: 12,
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            maxHeight: "25rem",
            overflow: "scroll",
          }}
        >
          {icons.map((icon, idx) => (
            <img
              key={icon.fileName || idx}
              src={icon.url}
              alt={icon.fileName || `icon-${idx}`}
              onClick={() => handleSelect(icon, type)}
              style={{
                background: theme === "dark" ? "#1f1f1f" : "#fafafa",
                width: 60,
                height: 60,
                cursor: "pointer",
                border:
                  selected?.url === icon.url
                    ? "2px solid #1890ff"
                    : "1px solid #ccc",
                borderRadius: 6,
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="icon-selector">
      {renderIcons(
        targetQuery.data,
        selectedTarget,
        "target",
        targetQuery.isLoading,
        targetQuery.isError
      )}
      {renderIcons(
        inkingQuery.data,
        selectedInking,
        "inking",
        inkingQuery.isLoading,
        inkingQuery.isError
      )}
    </div>
  );
};

export default IconSelector;
